import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';
import { getAudioFileUrl } from '@culture/utils/helpers/files';
import cx from 'classnames';

import getConfig from 'next/config';

import { ImageFormatContext } from 'contexts/ImageFormatContext';
import AudioPlayer from 'components/Audio/AudioPlayer';
import StyledContentImage from 'components/StyledContent/components/StyledContentImage';
import { InteractiveQuestionPropType, VariantPropType } from 'propTypes/common';
// eslint-disable-next-line import/no-cycle
import StyledContentBlock from 'components/StyledContent/components/StyledContentBlock';
import StyledContentVideo from 'components/StyledContent/components/StyledContentVideo';

import { getRatioImage } from 'utils/helpers/images';

import Variant from '../Variant';
import CorrectAnswer from '../CorrectAnswer';
import Matchings from '../Matchings';

import styles from './styles.module.scss';

const { publicRuntimeConfig } = getConfig();

const Question = ({
	question,
	selectVariant,
	selectedVariants,
	questionNumber,
	isTestCorrect,
	isTestFinished,
	interactiveMode,
	isSubmitted,
	totalQuestions,
	interactiveType
}) => {
	const imageFormat = useContext(ImageFormatContext);
	const { audio, jsonText, variants, image, answer, video, mode } = question;
	const isTestTree = interactiveType === 'testTree';

	const imageSrc =
		image &&
		image?.file &&
		getImageUrl(image.file, {
			format: imageFormat,
			params: {
				crop: 'fill',
				gravity: 'center'
			}
		});

	const trackUrl =
		!_.isEmpty(audio) &&
		getAudioFileUrl(audio, {
			config: publicRuntimeConfig
		});

	const isSelected = !_.isEmpty(selectedVariants[questionNumber]);
	const isSelectedAndCorrect =
		isSelected && selectedVariants[questionNumber]?.isCorrect;
	const questionWrapMediaCx = cx(styles.Question__WrapMedia, {
		[styles.Question__WrapMedia_float]: !!imageSrc && image.float
	});

	const isGridLayout = !!variants.find(
		(variant) => !!variant.video || !!variant.image
	);

	const renderVariants = () => {
		if (mode === 'matchings') {
			return (
				<Matchings
					{...{
						variants,
						selectVariant,
						questionNumber
					}}
				/>
			);
		}

		return variants.map((variant) => (
			<Variant
				key={
					variant.text ||
					variant.image?._id ||
					variant.audio?._id ||
					variant.video?._id
				}
				isQuestionSelected={isSelected}
				{...{
					variant,
					selectedVariants,
					selectVariant,
					questionNumber,
					isTestFinished,
					interactiveMode,
					isTestCorrect,
					mode
				}}
			/>
		));
	};

	const questionContent = (
		<>
			{!!video && (
				<div className={styles.Question__WrapMedia}>
					<div className={styles.Question__WrapVideo}>
						<StyledContentVideo
							material={{ _id: video }}
							autoPlay={false}
							responsive
						/>
					</div>
				</div>
			)}

			{!!trackUrl && (
				<AudioPlayer
					trackSrc={trackUrl}
					className={styles.Question__Audio}
					autoPlay={false}
				/>
			)}

			{!!imageSrc && (
				<div className={questionWrapMediaCx}>
					<div
						className={styles.Question__WrapImage}
						style={{ paddingBottom: `${getRatioImage(image.file)}%` }}
					>
						<StyledContentImage
							src={imageSrc}
							className={styles.Question__Image}
							lazy={interactiveMode !== 'slider'}
						/>
					</div>
				</div>
			)}

			<ul
				className={cx(
					styles.VariantsList,
					isGridLayout && styles.VariantsList_grid
				)}
			>
				{renderVariants()}
			</ul>
		</>
	);

	const answerContent = (
		<CorrectAnswer {...{ answer, isCorrect: isSelectedAndCorrect }} />
	);

	return (
		<li className={cx(styles.Question)}>
			<span
				className={cx(styles.Question__Text, {
					[styles.Question__Text_noCounter]: isTestTree
				})}
				style={{ counterSet: isTestTree ? null : `total ${totalQuestions}` }}
			>
				{jsonText.map((block) => (
					<StyledContentBlock
						key={block?.text?.slice(0, 15) || block?.type}
						{...block}
					/>
				))}
			</span>
			{mode === 'matchings' && (
				<div className={styles.Question__Hint}>
					Перетащите карточки для соответствия значений
				</div>
			)}
			{interactiveMode === 'slider' ? (
				<>
					{answer && isSelected && isSubmitted
						? answerContent
						: questionContent}
				</>
			) : (
				<>
					{questionContent}
					{!_.isEmpty(answer?.jsonText) && isSelected && answerContent}
				</>
			)}
		</li>
	);
};

Question.propTypes = {
	question: InteractiveQuestionPropType,
	selectedVariants: PropTypes.arrayOf(
		PropTypes.oneOfType([VariantPropType, PropTypes.object])
	),
	selectVariant: PropTypes.func,
	questionNumber: PropTypes.number,
	isTestCorrect: PropTypes.bool,
	isTestFinished: PropTypes.bool,
	interactiveMode: PropTypes.string,
	isSubmitted: PropTypes.bool,
	totalQuestions: PropTypes.number,
	showTotalQuestions: PropTypes.bool,
	interactiveType: PropTypes.string
};

export default Question;
